import { useTranslation } from "react-i18next";
import { FormContainer, Treturn, useFetch } from "@4uhub/lib4uhub";
import { memo, useCallback } from "react";
import { Grid } from "@mui/material";
import {
  offerClassificationSchema,
  TOfferClassificationForm,
} from "./offerClassificationSchema";
import {
  createTicketAutomationReceivedRuleOfferClassif,
  getTicketAutomationReceivedRuleOfferClassifById,
  updateTicketAutomationReceivedRuleOfferClassifById,
} from "../../../../../../../services/ticketAutomationReceivedRuleOfferClassif.service";
import { IOfferClassificationProps } from "./models";
import { OfferClassificationFields } from "./OfferClassificationFields";

const translationPath =
  "chatBot.page.automations.automationReceivedRules.ticketAutomationReceivedRuleOfferClassif.";

export const OfferClassification = memo(
  ({
    id,
    ticketAutomationReceivedRuleId,
    onBackButton,
  }: IOfferClassificationProps) => {
    const { t } = useTranslation();

    const { sendRequest: save, loading: sLoading } = useFetch(
      createTicketAutomationReceivedRuleOfferClassif,
    );

    const { sendRequest: update, loading: uLoading } = useFetch(
      updateTicketAutomationReceivedRuleOfferClassifById,
    );

    const { sendRequest: get, loading: gLoading } = useFetch(
      getTicketAutomationReceivedRuleOfferClassifById,
    );

    const updateHandler = (v: TOfferClassificationForm, id: string) =>
      update({
        item: {
          id: id,
          ...v,
          ticketAutomationReceivedRule: ticketAutomationReceivedRuleId ?? "",
          defaultClassifExternalId: v.defaultClassifExternal?.id,
          defaultClassifExternalName: v.defaultClassifExternal?.name,
          ticketAutomationReceivedRuleOfferClassifClassifications:
            v.classifications?.map((item) => ({
              externalId: item.id,
              externalName: item.name,
            })),
        },
        id: id,
      });

    const saveHandler = (v: TOfferClassificationForm) =>
      save({
        ...v,
        ticketAutomationReceivedRule: ticketAutomationReceivedRuleId ?? "",
        defaultClassifExternalId: v.defaultClassifExternal?.id,
        defaultClassifExternalName: v.defaultClassifExternal?.name,
        ticketAutomationReceivedRuleOfferClassifClassifications:
          v.classifications?.map((item) => ({
            externalId: item.id,
            externalName: item.name,
          })),
      });

    const getHandler = useCallback(
      async (id: string): Promise<Treturn<TOfferClassificationForm>> => {
        const { data, success } = await get(id);

        if (data) {
          const newData: TOfferClassificationForm = {
            ...data,
            defaultClassifExternal:
              data.defaultClassifExternalId && data.defaultClassifExternalName
                ? {
                    id: data.defaultClassifExternalId,
                    name: data.defaultClassifExternalName,
                  }
                : undefined,
            classifications:
              data.ticketAutomationReceivedRuleOfferClassifClassifications?.map(
                (item) => ({
                  id: item.externalId,
                  name: item.externalName,
                }),
              ),
          };
          return { data: newData, success: true };
        }
        return { data, success };
      },
      [get],
    );

    const loading = sLoading || uLoading || gLoading;

    return (
      <FormContainer<TOfferClassificationForm>
        saveFunction={saveHandler}
        updateFunction={updateHandler}
        getFunction={getHandler}
        loading={loading}
        title={t(translationPath + "offer_classification")}
        schema={offerClassificationSchema}
        subtitle={t(translationPath + "new_offer_classification")}
        backButtonOnClick={onBackButton}
        redirectOnSave={false}
        onSuccess={onBackButton}
        isModalForm={true}
        propId={id}
        formActionsOnBottom={false}
        fields={() => (
          <Grid container spacing={2}>
            <OfferClassificationFields />
          </Grid>
        )}
      />
    );
  },
);
